import React, { useState } from "react";
import { useTranslation } from "../contexts/TranslationContext";
import "./PopupGift.scss"; // Ensure this file contains the custom styles
import ConfirmPopup from "./ConfirmPopup";
import { adminGrant } from "../api/apiServiceFetch";

const DEFAULT_UNIT = "month";
// The amount fields hold the raw text, not a number. Keeping a number here is
// what produced "05": clearing the box gave Number("") === 0, the 0 was written
// straight back, and the next keystroke landed after it.
const EMPTY_AMOUNT = "";

const UNITS = [
	{ value: "day", labelKey: "grant_period_day" },
	{ value: "week", labelKey: "grant_period_week" },
	{ value: "month", labelKey: "grant_period_month" },
	{ value: "year", labelKey: "grant_period_year" },
];

// The largest value the API's int32 fields can carry. Not a product limit - the
// admin may hand out whatever they type, and the backend keeps the resulting end
// date inside the calendar - it only stops a number that JSON binding would
// refuse with an unexplained 400.
const MAX_WIRE_VALUE = 2147483647;

// Digits only, and an empty box stays empty. A leading zero is dropped so "0"
// followed by "5" reads 5 rather than 05.
const toAmountText = (value) => {
	const digits = String(value ?? "").replace(/\D/g, "");
	const trimmed = digits.replace(/^0+(?=\d)/, "");
	if (trimmed.length > 10 || Number(trimmed) > MAX_WIRE_VALUE) {
		return String(MAX_WIRE_VALUE);
	}
	return trimmed;
};

// An empty box means "nothing of this kind", same as zero.
const toAmountNumber = (value) => {
	const parsed = Number.parseInt(value, 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const PopupGift = ({ isOpen, onClose, userId, setVip }) => {
	const [showConfirm, setShowConfirm] = useState(false);
	const [premiumUnit, setPremiumUnit] = useState(DEFAULT_UNIT);
	const [premiumCount, setPremiumCount] = useState(EMPTY_AMOUNT);
	const [superLikes, setSuperLikes] = useState(EMPTY_AMOUNT);
	const [cleanSlate, setCleanSlate] = useState(EMPTY_AMOUNT);
	const [incognitoUnit, setIncognitoUnit] = useState(DEFAULT_UNIT);
	const [incognitoCount, setIncognitoCount] = useState(EMPTY_AMOUNT);
	const [note, setNote] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState(null);
	const { t } = useTranslation();

	// The amounts actually sent. The text state is for the boxes only.
	const premiumCountValue = toAmountNumber(premiumCount);
	const superLikesValue = toAmountNumber(superLikes);
	const cleanSlateValue = toAmountNumber(cleanSlate);
	const incognitoCountValue = toAmountNumber(incognitoCount);

	const hasNothingToGrant =
		premiumCountValue <= 0 &&
		superLikesValue <= 0 &&
		cleanSlateValue <= 0 &&
		incognitoCountValue <= 0;

	const resetFields = () => {
		setPremiumUnit(DEFAULT_UNIT);
		setPremiumCount(EMPTY_AMOUNT);
		setSuperLikes(EMPTY_AMOUNT);
		setCleanSlate(EMPTY_AMOUNT);
		setIncognitoUnit(DEFAULT_UNIT);
		setIncognitoCount(EMPTY_AMOUNT);
		setNote("");
		setError(null);
		setShowConfirm(false);
		setSubmitting(false);
	};

	// The count stays as typed when the unit changes: there is no per-unit
	// ceiling to pull it down to any more.
	const handlePremiumUnitChange = (event) => {
		setPremiumUnit(event.target.value);
	};

	const handlePremiumCountChange = (event) => {
		setPremiumCount(toAmountText(event.target.value));
	};

	const handleIncognitoUnitChange = (event) => {
		setIncognitoUnit(event.target.value);
	};

	const handleIncognitoCountChange = (event) => {
		setIncognitoCount(toAmountText(event.target.value));
	};

	const handleSuperLikesChange = (event) => {
		setSuperLikes(toAmountText(event.target.value));
	};

	const handleCleanSlateChange = (event) => {
		setCleanSlate(toAmountText(event.target.value));
	};

	const handleNoteChange = (event) => {
		setNote(event.target.value);
	};

	const handleSaveClick = () => {
		if (hasNothingToGrant) {
			setError(t("grant_error_nothing"));
			return;
		}
		setError(null);
		setShowConfirm(true);
	};

	const handleCancelConfirm = () => {
		setShowConfirm(false);
	};

	const handleConfirm = async () => {
		if (hasNothingToGrant) {
			setShowConfirm(false);
			setError(t("grant_error_nothing"));
			return;
		}

		setShowConfirm(false);
		setSubmitting(true);
		setError(null);

		try {
			await adminGrant({
				userId,
				premiumUnit,
				premiumCount: premiumCountValue,
				superLikes: superLikesValue,
				cleanSlate: cleanSlateValue,
				incognitoUnit,
				incognitoCount: incognitoCountValue,
				note,
			});

			if (premiumCountValue > 0) {
				setVip?.(true);
			}

			resetFields();
			onClose();
		} catch (err) {
			console.error("Grant error:", err);
			// The backend explains refusals it can (incognito is sold to women
			// only, unknown period unit); anything else falls back to the
			// generic message.
			const serverMessage =
				err && typeof err.message === "string" && err.message.trim()
					? err.message.trim()
					: "";
			setError(
				serverMessage && serverMessage !== "Bad Request"
					? serverMessage
					: t("grant_error_failed"),
			);
			setSubmitting(false);
		}
	};

	const handleClose = () => {
		resetFields();
		onClose();
	};

	if (!isOpen) return null;

	const renderUnitOptions = () =>
		UNITS.map((unit) => (
			<option key={unit.value} value={unit.value}>
				{t(unit.labelKey)}
			</option>
		));

	return (
		<div className="PopupGift-overlay">
			<ConfirmPopup
				isOpen={showConfirm}
				onConfirm={handleConfirm}
				onCancel={handleCancelConfirm}
			/>
			<div className="PopupGift-popup">
				<h2>{t("grant_title")}</h2>
				<div className="options">
					<p className="grant-hint">{t("grant_hint_extends")}</p>
				</div>
				<div className="grant-period">
					<label>
						{t("grant_premium_period")}
						<select
							value={premiumUnit}
							onChange={handlePremiumUnitChange}
							disabled={submitting}
						>
							{renderUnitOptions()}
						</select>
					</label>
					<label>
						{t("grant_premium_count")}
						<input
							type="number"
							min={0}
							value={premiumCount}
							onChange={handlePremiumCountChange}
							disabled={submitting}
						/>
					</label>
				</div>
				<div className="superlikes-input">
					<label>
						{t("grant_super_likes")}
						<input
							type="number"
							min={0}
							value={superLikes}
							onChange={handleSuperLikesChange}
							disabled={submitting}
						/>
					</label>
				</div>
				<div className="superlikes-input">
					<label>
						{t("grant_clean_slate")}
						<input
							type="number"
							min={0}
							value={cleanSlate}
							onChange={handleCleanSlateChange}
							disabled={submitting}
						/>
					</label>
				</div>
				<div className="grant-period">
					<label>
						{t("grant_incognito")}
						<select
							value={incognitoUnit}
							onChange={handleIncognitoUnitChange}
							disabled={submitting}
						>
							{renderUnitOptions()}
						</select>
					</label>
					<label>
						{t("grant_incognito_count")}
						<input
							type="number"
							min={0}
							value={incognitoCount}
							onChange={handleIncognitoCountChange}
							disabled={submitting}
						/>
					</label>
				</div>
				<div className="superlikes-input">
					<label>
						{t("cupid_col_note")}
						<input
							type="text"
							value={note}
							onChange={handleNoteChange}
							disabled={submitting}
						/>
					</label>
				</div>
				<p className="grant-hint">{t("grant_hint_zero")}</p>
				{error && <p className="grant-error">{error}</p>}
				<div className="popup-buttons">
					<button
						onClick={handleSaveClick}
						className="save-button"
						disabled={hasNothingToGrant || submitting}
					>
						{t("grant_submit")}
					</button>
					<button
						onClick={handleClose}
						className="cancel-button"
						disabled={submitting}
					>
						{t("grant_cancel")}
					</button>
				</div>
			</div>
		</div>
	);
};

export default PopupGift;
